<template>
  <div class="vx-row">
    <div class="vx-col w-full lg:w-1/2">
      <vx-card title="Verificación en dos pasos">
        <p class="security__description">
          Además de tu contraseña, al iniciar sesión te pedimos un código. Así nadie puede entrar
          a tu cuenta solo con tu contraseña.
        </p>

        <div v-if="loading" class="security__muted">Cargando…</div>

        <template v-else-if="!changing">
          <div class="security__current">
            <feather-icon :icon="methodIcon" svgClasses="w-6 h-6" class="security__current-icon"/>
            <div>
              <strong>{{ methodLabel }}</strong>
              <p v-if="status && status.configured_at" class="security__muted">
                Configurado el {{ configuredAt }}
              </p>
              <p v-else class="security__muted">
                Se te pedirá configurarlo la próxima vez que entres con tu contraseña.
              </p>
            </div>
          </div>

          <vs-button class="mt-5" color="primary" type="filled" @click="changing = true">
            {{ status && status.method ? 'Cambiar método' : 'Configurar ahora' }}
          </vs-button>

          <!-- Navegadores donde se marcó "Recordar por 30 días" al iniciar sesión. -->
          <div class="security__trusted mt-6">
            <div class="security__trusted-header">
              <div>
                <strong>Navegadores recordados</strong>
                <p class="security__muted">{{ trustedSummary }}</p>
              </div>
              <vs-button
                v-if="trusted.length > 1"
                type="border"
                color="danger"
                size="small"
                :disabled="forgetting"
                @click="confirmForgetAll">Olvidar todos</vs-button>
            </div>

            <ul v-if="trusted.length" class="security__devices">
              <li v-for="browser in trusted" :key="browser.id" class="security__device">
                <feather-icon :icon="browser.mobile ? 'SmartphoneIcon' : 'MonitorIcon'" svgClasses="w-5 h-5" class="security__device-icon"/>
                <div class="security__device-info">
                  <strong>{{ browser.name }}</strong>
                  <span v-if="browser.current" class="security__badge">Este navegador</span>
                  <p class="security__muted">
                    <span v-if="browser.ip">IP {{ browser.ip }} · </span>Recordado el {{ browser.createdAt }} · {{ browser.lastUsed }} · Vence el {{ browser.expiresAt }}
                  </p>
                </div>
                <vs-button
                  type="border"
                  color="danger"
                  size="small"
                  :disabled="forgetting"
                  @click="confirmForgetOne(browser)">Olvidar</vs-button>
              </li>
            </ul>
          </div>
        </template>

        <two-factor-method-setup
          v-else
          mode="settings"
          :current-method="status && status.method"
          @completed="onChanged"
          @cancel="changing = false"/>
      </vx-card>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { twoFactorService } from '@/_services';
import { trustedDevice } from '@/_helpers';
import TwoFactorMethodSetup from '@/views/pages/auth/two-factor/components/TwoFactorMethodSetup';

const METHODS = {
  TOTP: { label: 'App autenticadora', icon: 'SmartphoneIcon' },
  EMAIL: { label: 'Código por correo', icon: 'MailIcon' }
};

// "Chrome en Windows" a partir del user agent. Edge, Opera y Samsung Internet se revisan
// antes que Chrome porque su user agent también dice "Chrome".
function describeUserAgent(userAgent) {
  const ua = userAgent || '';
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\/|Opera/.test(ua) ? 'Opera' :
    /SamsungBrowser/.test(ua) ? 'Samsung Internet' :
    /Firefox\/|FxiOS/.test(ua) ? 'Firefox' :
    /Chrome\/|CriOS/.test(ua) ? 'Chrome' :
    /Safari\//.test(ua) ? 'Safari' : 'Navegador';
  const system =
    /iPhone/.test(ua) ? 'iPhone' :
    /iPad/.test(ua) ? 'iPad' :
    /Android/.test(ua) ? 'Android' :
    /Windows/.test(ua) ? 'Windows' :
    /Macintosh|Mac OS X/.test(ua) ? 'Mac' :
    /Linux/.test(ua) ? 'Linux' : null;
  return {
    name: system ? `${browser} en ${system}` : browser,
    mobile: /iPhone|iPad|Android|Mobile/.test(ua)
  };
}

// Seguridad de la cuenta: muestra el método de doble factor y permite cambiarlo (por
// ejemplo, al cambiar de celular). El método nuevo se confirma con su propio código.
// También lista los navegadores con "Recordar por 30 días" y permite olvidar uno o todos.
export default {
  name: 'Security',
  components: { TwoFactorMethodSetup },
  data() {
    return {
      status: null,
      loading: true,
      changing: false,
      trusted: [],
      forgetting: false
    }
  },
  computed: {
    trustedSummary() {
      if (!this.trusted.length) {
        return 'Ninguno: en todos los navegadores se pide el código.';
      }
      return 'En estos navegadores se marcó "Recordar por 30 días" y entran sin código. Si ya no usas uno o perdiste un equipo, olvídalo.';
    },
    methodLabel() {
      const method = this.status && METHODS[this.status.method];
      return method ? method.label : 'Sin configurar';
    },
    methodIcon() {
      const method = this.status && METHODS[this.status.method];
      return method ? method.icon : 'ShieldOffIcon';
    },
    configuredAt() {
      return moment(this.status.configured_at).format('DD/MM/YYYY');
    }
  },
  created() {
    this.loadStatus();
    this.loadTrusted();
  },
  methods: {
    loadTrusted() {
      return twoFactorService.listTrustedDevices()
        .then(items => {
          // El navegador actual primero.
          this.trusted = items.map(this.toBrowser).sort((a, b) => Number(b.current) - Number(a.current));
        }, error => {
          this.$store.dispatch('alert/error', (error && error.message) || error);
        });
    },
    toBrowser(item) {
      const attributes = item.attributes;
      const { name, mobile } = describeUserAgent(attributes.user_agent);
      return {
        id: item.id,
        name,
        mobile,
        ip: attributes.ip,
        current: attributes.current,
        createdAt: moment(attributes.created_at).format('DD/MM/YYYY'),
        lastUsed: attributes.last_used_at
          ? `Último uso el ${moment(attributes.last_used_at).format('DD/MM/YYYY HH:mm')}`
          : 'Aún sin usar',
        expiresAt: moment(attributes.expires_at).format('DD/MM/YYYY')
      };
    },
    loadStatus() {
      this.loading = true;
      return twoFactorService.getStatus()
        .then(status => {
          this.status = status;
        }, error => {
          this.$store.dispatch('alert/error', (error && error.message) || error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    confirmForgetOne(browser) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Olvidar navegador',
        text: `${browser.current ? 'Este navegador' : browser.name} volverá a pedir el código al iniciar sesión. ¿Deseas continuar?`,
        acceptText: 'Sí',
        cancelText: 'No',
        accept: () => this.forgetOne(browser)
      });
    },
    forgetOne(browser) {
      this.forgetting = true;
      twoFactorService.forgetTrustedDevice(browser.id)
        .then(() => {
          if (browser.current) {
            trustedDevice.clear();
          }
          this.$vs.notify({ title: 'Listo', text: `${browser.name} ya no está recordado.`, color: 'success', iconPack: 'feather', icon: 'icon-check-circle' });
        }, error => {
          this.$store.dispatch('alert/error', (error && error.message) || error);
        })
        // Se recarga también si falló (p. ej. ya lo había olvidado otra pestaña).
        .then(() => this.loadTrusted())
        .finally(() => {
          this.forgetting = false;
        });
    },
    confirmForgetAll() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Olvidar navegadores recordados',
        text: 'Todos los navegadores, incluido este, volverán a pedir el código al iniciar sesión. ¿Deseas continuar?',
        acceptText: 'Sí',
        cancelText: 'No',
        accept: this.forgetAll
      });
    },
    forgetAll() {
      this.forgetting = true;
      twoFactorService.forgetTrustedDevices()
        .then(() => {
          trustedDevice.clear();
          this.$vs.notify({ title: 'Listo', text: 'Ningún navegador queda recordado.', color: 'success', iconPack: 'feather', icon: 'icon-check-circle' });
        }, error => {
          this.$store.dispatch('alert/error', (error && error.message) || error);
        })
        .then(() => this.loadTrusted())
        .finally(() => {
          this.forgetting = false;
        });
    },
    onChanged(method) {
      this.changing = false;
      // Al cambiar de método la API olvida los navegadores recordados.
      trustedDevice.clear();
      this.loadTrusted();
      this.$vs.notify({
        title: 'Listo',
        text: `Desde ahora te pediremos el código de tu ${method === 'TOTP' ? 'app autenticadora' : 'correo'}.`,
        color: 'success',
        iconPack: 'feather',
        icon: 'icon-check-circle'
      });
      this.loadStatus();
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_colorsVariables.scss';

.security__description {
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 20px;
}

.security__current {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;

  strong {
    font-size: 1rem;
  }
}

.security__trusted {
  padding-top: 18px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.security__trusted-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  strong {
    font-size: 0.95rem;
  }
}

.security__devices {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
}

.security__device {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  &:first-child {
    border-top: none;
  }
}

.security__device-icon {
  color: rgba(0, 0, 0, 0.55);
  flex-shrink: 0;
}

.security__device-info {
  flex: 1;
  min-width: 0;

  strong {
    font-size: 0.92rem;
  }
}

.security__badge {
  margin-left: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  color: $primary;
  background: rgba(115, 103, 240, 0.12);
  border-radius: 999px;
  padding: 1px 8px;
}

.security__current-icon {
  color: $primary;
  flex-shrink: 0;
}

.security__muted {
  margin: 2px 0 0;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.55);
}
</style>
